import { ALL_COPY_ENTITIES, COPY_ENTITIES, CopyEntity, ExistingMode } from '../types/copy-paste.types';
import { Divider, SectionHeader } from '../../../../../shared/styles/components/layout.styles';
import { FlexColumn, FlexRow } from './CopyPaste.styles';
import { PastePreview, usePartPreviews } from './PastePreview';
import { RadioDesc, RadioGroup, RadioOption, RadioTitle } from '../../../../../shared/styles/components/forms.styles';
import React from 'react';

import Button from '@ingka/button';
import { Caption, SectionLabel } from '../../../../../shared/styles/components/primitives.styles';
import Checkbox from '@ingka/checkbox';
import type { Item } from '../../../types/item.types';
import { SubItem } from '../../../types/sub-item.types';
import { canCreateMissingParts } from '../utils/copy-paste.utils';

interface CopyOptionsSectionProps {
    copiedParts: SubItem[];
    resolvedSelectedItems: Item[];
    verifiedPartIds: string[];
    verifiedItemNos: string[];
    selectedCopyEntities: CopyEntity[];
    onChangeSelectedEntities: (entities: CopyEntity[]) => void;
    existingMode: ExistingMode;
    onChangeExistingMode: (mode: ExistingMode) => void;
    createMissing: boolean;
    onChangeCreateMissing: (value: boolean) => void;
    previewItemNo: string;
    onChangePreviewItemNo: (itemNo: string) => void;
    isSubmitting: boolean;
}

export const CopyOptionsSection = ({
    copiedParts,
    resolvedSelectedItems,
    verifiedPartIds,
    verifiedItemNos,
    selectedCopyEntities,
    onChangeSelectedEntities,
    existingMode,
    onChangeExistingMode,
    createMissing,
    onChangeCreateMissing,
    previewItemNo,
    onChangePreviewItemNo,
    isSubmitting,
}: CopyOptionsSectionProps) => {
    const allEntitiesSelected = selectedCopyEntities.length === ALL_COPY_ENTITIES.length;

    const toggleAllEntities = () => {
        onChangeSelectedEntities(allEntitiesSelected ? [] : ALL_COPY_ENTITIES);
    };

    const toggleEntity = (key: CopyEntity) => {
        onChangeSelectedEntities(
            selectedCopyEntities.includes(key)
                ? selectedCopyEntities.filter((e) => e !== key)
                : [...selectedCopyEntities, key]
        );
    };

    const { previewCandidates, targetItem, partPreviews, hasExisting, hasNew, hasPreview } =
        usePartPreviews({
            copiedParts,
            resolvedSelectedItems,
            verifiedPartIds,
            verifiedItemNos,
            selectedCopyEntities,
            existingMode,
            previewItemNo,
        });

    const canCreate = canCreateMissingParts(copiedParts);

    return (
        <>
            <SectionHeader>
                <SectionLabel>Copy options</SectionLabel>
                <Button type='tertiary' small onClick={toggleAllEntities} disabled={isSubmitting}>
                    {allEntitiesSelected ? 'Deselect all' : 'Select all'}
                </Button>
            </SectionHeader>

            <FlexColumn>
                <FlexRow>
                    {(Object.entries(COPY_ENTITIES) as [CopyEntity, string][]).map(([key, label]) => (
                        <Checkbox
                            key={key}
                            id={`entity-${key}`}
                            value={key}
                            label={label}
                            checked={selectedCopyEntities.includes(key)}
                            disabled={isSubmitting}
                            onChange={() => toggleEntity(key)}
                        />
                    ))}
                </FlexRow>

                <Divider />

                {hasPreview && targetItem && (
                    <>
                        {hasExisting && (
                            <ExistingModeRadio
                                existingMode={existingMode}
                                onChangeExistingMode={onChangeExistingMode}
                                isSubmitting={isSubmitting}
                            />
                        )}

                        {hasNew && (
                            <CreateMissingCheckbox
                                createMissing={createMissing}
                                onChangeCreateMissing={onChangeCreateMissing}
                                canCreate={canCreate}
                                isSubmitting={isSubmitting}
                            />
                        )}

                        <PastePreview
                            partPreviews={partPreviews}
                            previewCandidates={previewCandidates}
                            targetItem={targetItem}
                            existingMode={existingMode}
                            createMissing={createMissing}
                            canCreate={canCreate}
                            onChangePreviewItemNo={onChangePreviewItemNo}
                            isSubmitting={isSubmitting}
                        />
                    </>
                )}
            </FlexColumn>
        </>
    );
};

const ExistingModeRadio = ({
    existingMode,
    onChangeExistingMode,
    isSubmitting,
}: {
    existingMode: ExistingMode;
    onChangeExistingMode: (m: ExistingMode) => void;
    isSubmitting: boolean;
}) => (
    <>
        <SectionLabel>Paste behavior (for existing parts)</SectionLabel>
        <RadioGroup>
            <RadioOption $disabled={isSubmitting}>
                <input
                    type='radio'
                    name='existing-mode'
                    id='existing-mode-overwrite'
                    checked={existingMode === 'overwrite'}
                    onChange={() => onChangeExistingMode('overwrite')}
                    disabled={isSubmitting}
                />
                <label htmlFor='existing-mode-overwrite'>
                    <RadioTitle>Overwrite completely</RadioTitle>
                    <RadioDesc>Replace all selected field values with the source values.</RadioDesc>
                </label>
            </RadioOption>
            <RadioOption $disabled={isSubmitting}>
                <input
                    type='radio'
                    name='existing-mode'
                    id='existing-mode-fill-missing'
                    checked={existingMode === 'fill-missing'}
                    onChange={() => onChangeExistingMode('fill-missing')}
                    disabled={isSubmitting}
                />
                <label htmlFor='existing-mode-fill-missing'>
                    <RadioTitle>Fill missing only</RadioTitle>
                    <RadioDesc>
                        Only fill in fields that are currently empty (blank/null). Existing values, including 0, are kept.
                    </RadioDesc>
                </label>
            </RadioOption>
            <RadioOption $disabled={isSubmitting}>
                <input
                    type='radio'
                    name='existing-mode'
                    id='existing-mode-skip'
                    checked={existingMode === 'skip'}
                    onChange={() => onChangeExistingMode('skip')}
                    disabled={isSubmitting}
                />
                <label htmlFor='existing-mode-skip'>
                    <RadioTitle>Skip existing parts</RadioTitle>
                    <RadioDesc>Leave existing parts untouched.</RadioDesc>
                </label>
            </RadioOption>
        </RadioGroup>
    </>
);

const CreateMissingCheckbox = ({
    createMissing,
    onChangeCreateMissing,
    canCreate,
    isSubmitting,
}: {
    createMissing: boolean;
    onChangeCreateMissing: (v: boolean) => void;
    canCreate: boolean;
    isSubmitting: boolean;
}) => (
    <>
        <SectionLabel>Missing parts</SectionLabel>
        <Checkbox
            id='create-missing'
            value='create-missing'
            label='Create missing parts on target items'
            checked={canCreate && createMissing}
            disabled={isSubmitting || !canCreate}
            onChange={() => onChangeCreateMissing(!createMissing)}
        />
        {!canCreate && (
            <Caption>
                Only user/application-created material sets can be created. None of the copied parts qualify.
            </Caption>
        )}
    </>
);
